import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import WhatsAppCard from "./WhatsAppCard";
import UpgradeModal from "./UpgradeModal";
import { BrainCircuit, ChevronDown, Copy, Loader2, Lock, LogOut, Play } from "lucide-react";
import TerminalMarkets from "./TerminalMarkets";
import EquityChart from "./EquityChart";
import AdvancedTicket from "./AdvancedTicket";
import PendingOrders from "./PendingOrders";
import VirtualCard from "./VirtualCard";
import TierPerks from "./TierPerks";
import MembershipBenefits from "./MembershipBenefits";
import PhysicalCardOrder from "./PhysicalCardOrder";
import CryptoPriceAlerts from "./CryptoPriceAlerts";
import UpgradeTier from "./UpgradeTier";
import Wallet from "./Wallet";
import BoostHistoryChart from "./BoostHistoryChart";
import AmbassadorBanner from "./AmbassadorBanner";
import BoostCountdown from "./BoostCountdown";
import LivePriceTicker from "./LivePriceTicker";
import FeaturedChart from "./FeaturedChart";
import FeaturedSymbolSwitcher, { SYMBOL_OPTIONS } from "./FeaturedSymbolSwitcher";
import MoneyFlow from "./MoneyFlow";
import TradingChat from "./TradingChat";
import StatsTable from "./StatsTable";
import SeedBreakdownCard from "./SeedBreakdownCard";
import WithdrawalCountdown from "./WithdrawalCountdown";
import SastWindowBadge from "./SastWindowBadge";
import { playChime, flashTitle } from "./boostBell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOST_TRADE_OPTIONS = [
  { v: "BTC", l: "BTC · Bitcoin" },
  { v: "ETH", l: "ETH · Ethereum" },
  { v: "SOL", l: "SOL · Solana" },
  { v: "GOLD", l: "GOLD · Bullion" },
  { v: "SILVER", l: "SILVER · Bullion" },
  { v: "OIL", l: "OIL · Crude" },
  { v: "AAPL", l: "AAPL · Apple" },
  { v: "NVDA", l: "NVDA · Nvidia" },
  { v: "TSLA", l: "TSLA · Tesla" },
  { v: "EURUSD", l: "EUR/USD" },
];

const fmt = (n, dp = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

export default function Terminal() {
  const [token, setToken] = useState(() => sessionStorage.getItem("aureon_terminal_token"));
  const [tab, setTab] = useState("markets");
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [account, setAccount] = useState(null);
  const [coins, setCoins] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activity, setActivity] = useState([]);
  const [busy, setBusy] = useState(null);

  const loadAll = useCallback(async (tok) => {
    const t = tok || token;
    if (!t) return;
    const headers = { Authorization: `Bearer ${t}` };
    try {
      const [a, o, ai] = await Promise.all([
        axios.get(`${API}/terminal/account`, { headers }),
        axios.get(`${API}/terminal/orders`, { headers }),
        axios.get(`${API}/terminal/ai/activity`, { headers }),
      ]);
      setAccount(a.data);
      setOrders(o.data.orders || []);
      setActivity(ai.data.activity || []);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("aureon_terminal_token");
        setToken(null);
      }
    }
  }, [token]);

  useEffect(() => {
    loadAll();
    const t = setInterval(() => loadAll(), 30000);
    return () => clearInterval(t);
  }, [loadAll]);

  useEffect(() => {
    axios.get(`${API}/markets/top`).then(({ data }) => setCoins(data.coins || [])).catch(() => {});
    axios.get(`${API}/markets/stocks`).then(({ data }) => setStocks(data.rows || [])).catch(() => {});
  }, []);

  const enter = async (e) => {
    e.preventDefault();
    setBusy("enter");
    setError(null);
    try {
      const { data } = await axios.post(`${API}/terminal/enter`, { code });
      sessionStorage.setItem("aureon_terminal_token", data.token);
      setToken(data.token);
      loadAll(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Access denied. Check your code.");
    } finally {
      setBusy(null);
    }
  };

  const quickSell = async (p, pct) => {
    setError(null);
    try {
      await axios.post(
        `${API}/terminal/trade`,
        { symbol: p.symbol, side: "sell", qty: p.qty * pct, order_type: "market" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Quick sell failed.");
    }
  };

  const toggleAi = async () => {
    await axios.post(`${API}/terminal/ai/toggle`, { on: !account?.auto_trade },
      { headers: { Authorization: `Bearer ${token}` } });
    loadAll();
  };

  const runAi = async () => {
    setBusy("ai");
    try {
      await axios.post(`${API}/terminal/ai/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      loadAll();
    } finally {
      setBusy(null);
    }
  };

  const [dailyCode, setDailyCode] = useState("");
  const [tradeSymbol, setTradeSymbol] = useState("BTC");
  const [redeemMsg, setRedeemMsg] = useState(null);
  const [redeemErr, setRedeemErr] = useState(null);
  const [withdrawMsg, setWithdrawMsg] = useState(null);
  const [withdrawErr, setWithdrawErr] = useState(null);
  const [wallet, setWallet] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);
  const dailyInputRef = useRef(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [walletSaveMsg, setWalletSaveMsg] = useState(null);
  const [walletSaveErr, setWalletSaveErr] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [profile, setProfile] = useState(null);
  const [boostHistory, setBoostHistory] = useState([]);
  const [todaysCode, setTodaysCode] = useState(null);
  const [autoTried, setAutoTried] = useState(false);
  const [featuredSymbol, setFeaturedSymbol] = useState("BTC");
  const [cardTab, setCardTab] = useState("overview");

  // Load today's boost code so the terminal can display + auto-redeem it
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/terminal/todays-code`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setTodaysCode(data);
        if (data?.code_string) setDailyCode(data.code_string);
      })
      .catch(() => setTodaysCode(null));
  }, [token, account?.daily_code_redeemed_today]);

  // Auto-redeem once per session on first load if today's code has not been claimed yet
  useEffect(() => {
    if (!token || autoTried || !todaysCode?.code_string) return;
    if (todaysCode.redeemed_today || account?.daily_code_redeemed_today) return;
    if (!account || (account.equity_usd ?? 0) < 100) return;
    setAutoTried(true);
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/terminal/daily-code`,
          { code: todaysCode.code_string, trade_symbol: "BTC" },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setRedeemMsg(`Auto-boost +$${fmt(data.bonus)} credited`);
        loadAll();
      } catch (e) {
        // silent — user can still redeem manually
      }
    })();
  }, [token, todaysCode, account?.daily_code_redeemed_today, account?.equity_usd, autoTried]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/client/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setProfile(data))
      .catch(() => setProfile(null));
    axios.get(`${API}/terminal/boost-history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setBoostHistory(data.history || []))
      .catch(() => setBoostHistory([]));
  }, [token, account?.equity_usd]);

  useEffect(() => {
    if (!token || tab !== "portfolio") return;
    axios.get(`${API}/terminal/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setLeaderboard(data))
      .catch(() => {});
  }, [token, tab, account?.referral_count]);

  useEffect(() => {
    if (account?.trc_wallet && !wallet) setWallet(account.trc_wallet);
  }, [account]);

  const redeemDaily = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setBusy("redeem");
    setRedeemMsg(null);
    setRedeemErr(null);
    try {
      const { data } = await axios.post(
        `${API}/terminal/daily-code`,
        { code: dailyCode, trade_symbol: tradeSymbol },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRedeemMsg(`+$${fmt(data.bonus)} credited · tagged ${tradeSymbol}`);
      setDailyCode("");
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      setRedeemErr(typeof d === "string" ? d : "Redemption failed.");
    } finally {
      setBusy(null);
    }
  };

  // One-tap claim: uses today's visible code directly, no paste required.
  const claimNow = async () => {
    if (!todaysCode?.code_string) return;
    setBusy("redeem");
    setRedeemMsg(null);
    setRedeemErr(null);
    try {
      const { data } = await axios.post(
        `${API}/terminal/daily-code`,
        { code: todaysCode.code_string, trade_symbol: tradeSymbol },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRedeemMsg(`+$${fmt(data.bonus)} credited`);
      setDailyCode("");
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      setRedeemErr(typeof d === "string" ? d : "Claim failed. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const withdraw = async () => {
    setBusy("withdraw");
    setWithdrawMsg(null);
    setWithdrawErr(null);
    setError(null);
    // Strip zero-width / invisible chars that can sneak in from mobile copy-paste
    const cleanWallet = (wallet || "").replace(/[\u200B-\u200D\uFEFF\s]/g, "");
    if (!/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(cleanWallet)) {
      setWithdrawErr("Invalid TRON address. It must start with T and be 34 characters (no spaces).");
      setBusy(null);
      return;
    }
    const max = Number(account?.withdrawable_usd ?? 0);
    const amt = parseFloat(withdrawAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setWithdrawErr("Enter how much profit you want to withdraw.");
      setBusy(null);
      return;
    }
    if (amt > max + 0.01) {
      setWithdrawErr(`You can only withdraw up to $${max.toFixed(2)} of profit.`);
      setBusy(null);
      return;
    }
    if (cleanWallet !== wallet) setWallet(cleanWallet);
    try {
      const { data } = await axios.post(
        `${API}/terminal/withdraw`,
        { wallet_address: cleanWallet, amount: amt },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWithdrawMsg(`Withdrawal request sent · $${fmt(data.amount)} → ${data.wallet.slice(0, 6)}…${data.wallet.slice(-4)}. Admin will pay externally within 24h.`);
      setWithdrawAmount("");
      loadAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      setWithdrawErr(typeof d === "string" ? d : "Withdrawal failed. Please try again or contact support.");
    } finally {
      setBusy(null);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6">
        <Lock className="mb-6 h-6 w-6 text-gold" />
        <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">Aureon · Private Terminal</p>
        <h1 className="font-serif text-4xl font-light tracking-tight text-white" data-testid="terminal-gate-title">
          Enter your <span className="italic text-gold">access code.</span>
        </h1>
        <form onSubmit={enter} className="mt-10 w-full max-w-sm" data-testid="terminal-gate-form">
          <input
            data-testid="terminal-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="AUR-XXXXXXXX"
            className="w-full border border-white/15 bg-surface px-5 py-4 text-center font-tabular text-lg tracking-[0.2em] text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
          />
          <button
            data-testid="terminal-enter-btn"
            type="submit"
            disabled={busy === "enter" || !code}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-50"
          >
            {busy === "enter" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock the terminal"}
          </button>
          {error && <p data-testid="terminal-gate-error" className="mt-4 text-center text-xs text-[#FF003C]">{error}</p>}
          <p className="mt-6 text-center text-[10px] uppercase tracking-[0.2em] text-white/30">
            No code? <a href="/#access" className="text-gold/80 hover:text-gold" data-testid="terminal-get-access-link">Get access at Aureon</a>
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-6 py-10 lg:px-10" data-testid="terminal-dashboard">
      <UpgradeModal token={token} />
      <div className="mx-auto max-w-7xl">
        <div className="flex items-center justify-between border-b border-white/10 pb-6">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl font-semibold tracking-tight text-white">AUREON</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Terminal</span>
            <span className="ml-2 flex items-center gap-2 self-center" data-testid="terminal-connection">
              <span className={`h-1.5 w-1.5 rounded-full ${account ? "bg-[#26A69A] animate-pulse-dot" : "bg-white/25"}`} />
              <span className={`text-[9px] uppercase tracking-[0.2em] ${account ? "text-[#26A69A]" : "text-white/30"}`}>
                {account ? "Connected" : "Connecting"}
              </span>
            </span>
            <SastWindowBadge />
          </div>
          <button
            data-testid="terminal-logout-btn"
            onClick={() => { sessionStorage.removeItem("aureon_terminal_token"); setToken(null); setAccount(null); }}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-gold"
          >
            <LogOut className="h-3.5 w-3.5" /> Lock
          </button>
        </div>

        <div className="mt-8 flex gap-2" data-testid="terminal-tabs">
          <button
            data-testid="tab-markets"
            onClick={() => setTab("markets")}
            className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "markets" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
            }`}
          >
            Markets
          </button>
          <button
            data-testid="tab-portfolio"
            onClick={() => setTab("portfolio")}
            className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "portfolio" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
            }`}
          >
            Portfolio
          </button>
          <button
            data-testid="tab-alerts"
            onClick={() => setTab("alerts")}
            className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "alerts" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
            }`}
          >
            Alerts
          </button>
          <button
            data-testid="tab-wallet"
            onClick={() => setTab("wallet")}
            className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "wallet" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
            }`}
          >
            Wallet
          </button>
          <button
            data-testid="tab-card"
            onClick={() => setTab("card")}
            className={`rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              tab === "card" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:border-gold hover:text-gold"
            }`}
          >
            My Card
          </button>
        </div>

        {tab === "markets" ? (
          <TerminalMarkets token={token} coins={coins} stocks={stocks} cash={account?.balance_usd} onTraded={() => loadAll()} />
        ) : tab === "alerts" ? (
          <CryptoPriceAlerts code={profile?.accessCode || account?.code} />
        ) : tab === "wallet" ? (
          <Wallet token={token} account={account} profile={profile} />
        ) : tab === "card" ? (
          <div className="mt-8 space-y-5">
            <div className="flex gap-2 border-b border-white/10" data-testid="card-subtabs">
              {[
                { k: "overview", l: "Overview" },
                { k: "benefits", l: "Benefits" },
                { k: "upgrade", l: "Upgrade" },
                { k: "physical", l: "Physical card" },
              ].map((s) => (
                <button
                  key={s.k}
                  data-testid={`card-subtab-${s.k}`}
                  onClick={() => setCardTab(s.k)}
                  className={`-mb-px px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] transition-colors ${
                    cardTab === s.k ? "border-b border-gold text-gold" : "text-white/45 hover:text-white/80"
                  }`}
                >
                  {s.l}
                </button>
              ))}
            </div>

            {cardTab === "overview" && (
              <div className="space-y-6">
                {profile?.isAmbassador && (
                  <AmbassadorBanner
                    name={profile?.fullName}
                    title={profile?.ambassadorTitle || "Group Ambassador"}
                    tierName={profile?.tierName}
                  />
                )}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
                  <div>
                    <VirtualCard
                      clientName={profile?.fullName}
                      balance={profile?.accountBalance ?? account?.equity_usd}
                      tier={profile?.accountTier || 100}
                      accessCode={profile?.accessCode}
                    />
                    <div className="mt-4">
                      <LivePriceTicker coins={coins} stocks={stocks} />
                    </div>
                  </div>
                  <TierPerks tier={profile?.accountTier || 100} />
                </div>
              </div>
            )}

            {cardTab === "benefits" && (
              <MembershipBenefits
                tier={profile?.accountTier || 100}
                createdAt={account?.created_at}
                token={token}
              />
            )}

            {cardTab === "upgrade" && (
              <div data-testid="terminal-upgrade-tier">
                <UpgradeTier
                  token={token}
                  currentTier={profile?.accountTier || 100}
                  accessCode={profile?.accessCode}
                />
              </div>
            )}

            {cardTab === "physical" && (
              <PhysicalCardOrder token={token} defaultName={profile?.fullName} />
            )}
          </div>
        ) : (
        <>
        {profile?.isAmbassador && (
          <div className="mb-6">
            <AmbassadorBanner
              name={profile?.fullName}
              title={profile?.ambassadorTitle || "Group Ambassador"}
              tierName={profile?.tierName}
            />
          </div>
        )}
        <EquityChart token={token} equity={account?.equity_usd} dayPnl={account?.day_pnl} />

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="border border-white/10 bg-surface p-6">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Equity</p>
            <p className="mt-2 font-serif text-4xl font-light text-white" data-testid="terminal-equity">
              ${fmt(account?.equity_usd)}
            </p>
          </div>
          <div className="border border-white/10 bg-surface p-6">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Free cash</p>
            <p className="mt-2 font-tabular text-2xl text-white/85" data-testid="terminal-cash">
              ${fmt(account?.balance_usd)}
            </p>
          </div>
          <div className="border border-white/10 bg-surface p-6">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Day P&L</p>
            <p className={`mt-2 font-tabular text-lg sm:text-2xl break-all ${(account?.day_pnl ?? 0) >= 0 ? "text-[#00FF41]" : "text-[#FF003C]"}`} data-testid="terminal-day-pnl">
              {(account?.day_pnl ?? 0) >= 0 ? "+" : "-"}${fmt(Math.abs(account?.day_pnl ?? 0))}
            </p>
          </div>
          <div className="border border-gold/30 bg-gold/[0.04] p-6">
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gold">
              <BrainCircuit className="h-3.5 w-3.5" /> AI auto-trader
            </p>
            <div className="mt-3 flex items-center gap-3">
              <button
                data-testid="terminal-ai-toggle"
                onClick={toggleAi}
                className={`rounded-full px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  account?.auto_trade ? "bg-gold text-ink" : "border border-gold/40 text-gold hover:bg-gold hover:text-ink"
                }`}
              >
                {account?.auto_trade ? "On" : "Off"}
              </button>
              <button
                data-testid="terminal-ai-run-btn"
                onClick={runAi}
                disabled={busy === "ai"}
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 transition-colors duration-300 hover:text-gold disabled:opacity-40"
              >
                {busy === "ai" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Run now
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="border border-gold/30 bg-gold/[0.04] p-6" data-testid="daily-code-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Daily code · boost</p>
            <p className="mt-2 text-xs leading-relaxed text-white/45">
              Copy today's code, paste it below and press Redeem to claim today's boost.
            </p>
            <BoostCountdown
              onZero={() => {
                try { playChime(); } catch {}
                try { flashTitle(); } catch {}
                // refresh today's code so the newly-generated one loads in
                if (token) {
                  axios
                    .get(`${API}/terminal/todays-code`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(({ data }) => {
                      setTodaysCode(data);
                      if (data?.code_string) setDailyCode(data.code_string);
                      setAutoTried(false);
                    })
                    .catch(() => {});
                }
              }}
            />
            {todaysCode?.code_string && (
              <div className="mt-3 border border-gold/25 bg-ink/40 p-3" data-testid="todays-code-panel">
                <p className="text-[9px] uppercase tracking-[0.28em] text-white/45">Today's code</p>
                <div className="mt-1 flex items-center gap-2">
                  <p className="flex-1 font-tabular text-sm tracking-[0.18em] text-gold" data-testid="todays-code-value">
                    {todaysCode.code_string}
                  </p>
                  <button
                    type="button"
                    data-testid="todays-code-copy"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(todaysCode.code_string); } catch {}
                      setDailyCode(todaysCode.code_string);
                      setCodeCopied(true);
                      setTimeout(() => setCodeCopied(false), 2200);
                    }}
                    className="flex items-center gap-1 rounded-full border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-gold hover:text-gold"
                  >
                    <Copy className="h-3 w-3" />
                    {codeCopied ? "Copied" : "Copy"}
                  </button>
                </div>
                {!account?.daily_code_redeemed_today && (
                  <button
                    type="button"
                    data-testid="claim-boost-btn"
                    onClick={claimNow}
                    disabled={busy === "redeem"}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold uppercase tracking-[0.22em] text-ink shadow-[0_0_24px_rgba(212,175,55,0.25)] transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_32px_rgba(212,175,55,0.4)] disabled:opacity-40"
                  >
                    {busy === "redeem" ? <Loader2 className="h-4 w-4 animate-spin" /> : "⚡ Claim today's boost"}
                  </button>
                )}
              </div>
            )}
            <BoostHistoryChart history={boostHistory} />
            {account?.daily_code_redeemed_today ? (
              <form className="mt-4 space-y-3 opacity-70">
                <div>
                  <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-gold" htmlFor="daily-code-input-locked">
                    Step 2 · Paste today's code (unlocks tomorrow)
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="daily-code-input-locked"
                      data-testid="daily-code-input-locked"
                      value="✓ Claimed today"
                      disabled
                      className="w-full cursor-not-allowed border border-[#00FF41]/30 bg-ink px-4 py-3 font-tabular text-sm tracking-[0.15em] text-[#00FF41] focus:outline-none"
                    />
                    <button
                      type="button"
                      disabled
                      data-testid="daily-code-claimed"
                      className="shrink-0 rounded-full border border-white/15 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white/40"
                    >
                      Claimed
                    </button>
                  </div>
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-white/50">
                    Next code arrives 08:00 Dubai. Paste it here and press Redeem to claim your daily boost.
                  </p>
                </div>
              </form>
            ) : (
              <form onSubmit={redeemDaily} className="mt-4 space-y-3">
                <details className="group">
                  <summary className="cursor-pointer text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-gold">
                    Prefer to paste manually? ▾
                  </summary>
                  <div className="mt-3 space-y-3">
                    <div>
                      <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-gold" htmlFor="daily-code-input">
                        Paste today's code
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="daily-code-input"
                          ref={dailyInputRef}
                          data-testid="daily-code-input"
                          value={dailyCode}
                          onChange={(e) => setDailyCode(e.target.value.toUpperCase())}
                          placeholder="Paste code here (AUR-XXXXXXXX)"
                          className={`w-full border bg-ink px-4 py-3 font-tabular text-sm tracking-[0.15em] text-white placeholder:text-white/30 focus:outline-none transition-colors ${
                            codeCopied ? "border-gold shadow-[0_0_18px_rgba(212,175,55,0.35)]" : "border-white/20 focus:border-gold"
                          }`}
                        />
                        <button
                          data-testid="daily-code-submit"
                          type="submit"
                          disabled={busy === "redeem" || !dailyCode.trim()}
                          className="shrink-0 rounded-full border border-gold/40 px-6 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-40"
                        >
                          {busy === "redeem" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-white/40" htmlFor="boost-trade">
                        Optional · tag with an asset
                      </label>
                      <select
                        id="boost-trade"
                        data-testid="daily-code-trade-select"
                        value={tradeSymbol}
                        onChange={(e) => setTradeSymbol(e.target.value)}
                        className="w-full border border-white/15 bg-ink px-4 py-2.5 font-tabular text-xs tracking-wide text-white focus:border-gold focus:outline-none"
                      >
                        {BOOST_TRADE_OPTIONS.map((o) => (
                          <option key={o.v} value={o.v}>{o.l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </details>
              </form>
            )}
            {redeemMsg && <p className="mt-3 text-xs text-[#00FF41]" data-testid="daily-code-success">{redeemMsg}</p>}
            {redeemErr && <p className="mt-3 text-xs text-[#FF003C]" data-testid="daily-code-error">{redeemErr}</p>}
          </div>

          <div className="border border-white/10 bg-surface p-6" data-testid="withdraw-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Withdraw · TRC-20</p>
            <p className="mt-2 font-tabular text-2xl text-white/85" data-testid="withdraw-amount">
              ${fmt(account?.withdrawable_usd ?? 0)}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">Available profit</p>
            {account?.withdrawal?.allowed ? (
              <>
                <label className="mt-4 block text-[9px] uppercase tracking-[0.25em] text-white/40" htmlFor="withdraw-amt">
                  How much profit do you want to withdraw?
                </label>
                <div className="mt-1 flex items-stretch gap-2">
                  <input
                    id="withdraw-amt"
                    data-testid="withdraw-amount-input"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="1"
                    max={account?.withdrawable_usd ?? 0}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder={`USD (max $${fmt(account?.withdrawable_usd ?? 0)})`}
                    className="w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
                  />
                  <button
                    type="button"
                    data-testid="withdraw-max-btn"
                    onClick={() => setWithdrawAmount(String((account?.withdrawable_usd ?? 0).toFixed(2)))}
                    className="shrink-0 rounded-full border border-gold/40 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
                  >
                    Max
                  </button>
                </div>
                <input
                  data-testid="withdraw-wallet-input"
                  value={wallet}
                  onChange={(e) => setWallet(e.target.value.replace(/[\u200B-\u200D\uFEFF\s]/g, ""))}
                  placeholder="Your TRON (TRC-20) wallet address"
                  className="mt-3 w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-xs tracking-wide text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
                />
                <button
                  data-testid="withdraw-btn"
                  onClick={withdraw}
                  disabled={busy === "withdraw" || (account?.withdrawable_usd ?? 0) <= 0 || !wallet || !withdrawAmount}
                  className="mt-3 flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-40"
                >
                  {busy === "withdraw" ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Withdrawing…</>
                  ) : "Withdraw profit"}
                </button>
                <p className="mt-2 text-[10px] leading-relaxed text-white/35">
                  Paid in USDT to your TRON wallet. One withdrawal every 14 days.
                </p>
                <p className="mt-1 text-[10px] leading-relaxed text-gold/70" data-testid="withdraw-hours-note">
                  Office hours · 10:00–18:00 SAST (South Africa). Requests placed after 18:00 are released the next business day when the window reopens.
                </p>
                {withdrawErr && (
                  <p className="mt-3 rounded border border-[#FF003C]/30 bg-[#FF003C]/5 p-2.5 text-[11px] leading-relaxed text-[#FF003C]" data-testid="withdraw-error">
                    {withdrawErr}
                  </p>
                )}
              </>
            ) : (
              <div className="mt-4" data-testid="withdraw-countdown">
                <button
                  disabled
                  className="cursor-not-allowed rounded-full border border-white/10 px-6 py-3 text-xs uppercase tracking-[0.2em] text-white/25"
                >
                  Withdraw locked
                </button>
                <WithdrawalCountdown
                  targetIso={account?.withdrawal?.next_available_at}
                  label="Withdrawal unlocks in"
                />
              </div>
            )}
            {withdrawMsg && (
              <p className="mt-3 rounded border border-[#00FF41]/30 bg-[#00FF41]/5 p-2.5 text-[11px] leading-relaxed text-[#00FF41]" data-testid="withdraw-success">
                {withdrawMsg}
              </p>
            )}
          </div>

          <div className="border border-white/10 bg-surface p-6" data-testid="invite-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Invite & earn</p>
            <p className="mt-2 text-xs leading-relaxed text-white/45">
              Friends who join with your code earn you 10% of their tier.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <span className="flex-1 border border-gold/30 bg-ink px-4 py-3 font-tabular text-sm tracking-[0.15em] text-gold" data-testid="invite-code">
                {account?.referral_code || "…"}
              </span>
              <button
                data-testid="invite-copy-btn"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(account?.referral_code || "");
                    setInviteCopied(true);
                    setTimeout(() => setInviteCopied(false), 2000);
                  } catch {}
                }}
                className="shrink-0 rounded-full border border-gold/40 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
              >
                {inviteCopied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="mt-3 text-[11px] text-white/40" data-testid="invite-stats">
              {account?.referral_count ?? 0} joined · ${fmt(account?.referral_earned ?? 0)} earned
            </p>
          </div>
        </div>

        {leaderboard && (
          <div className="mt-4 border border-white/10 bg-surface p-6" data-testid="leaderboard-card">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Top inviters</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/35" data-testid="leaderboard-your-rank">
                {leaderboard.you.rank
                  ? `Your rank · #${leaderboard.you.rank} of ${leaderboard.total_inviters}`
                  : "Share your invite code to enter"}
              </p>
            </div>
            {leaderboard.rows.length === 0 ? (
              <p className="mt-4 text-xs text-white/35" data-testid="leaderboard-empty">
                No inviters yet — be the first. Your invite code is on the card above.
              </p>
            ) : (
              <div className="mt-4">
                {leaderboard.rows.map((r) => (
                  <div
                    key={r.rank}
                    data-testid={`leaderboard-row-${r.rank}`}
                    className={`flex items-center justify-between border-b border-white/5 py-2.5 text-xs last:border-b-0 ${
                      r.you ? "text-gold" : "text-white/70"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`font-tabular ${r.rank <= 3 ? "text-gold" : "text-white/35"}`}>
                        {String(r.rank).padStart(2, "0")}
                      </span>
                      <span className="font-tabular tracking-[0.15em]">
                        {r.label}
                        {r.you && <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-gold" data-testid="leaderboard-you">You</span>}
                      </span>
                    </span>
                    <span className="flex items-baseline gap-4 font-tabular">
                      <span className="text-white/45">{r.referrals} joined</span>
                      <span className={r.you ? "text-gold" : "text-white/85"}>${fmt(r.earned)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Featured live chart + money flow panel — big animated visuals */}
        <div className="mt-6 space-y-3" data-testid="portfolio-featured">
          <FeaturedSymbolSwitcher value={featuredSymbol} onChange={setFeaturedSymbol} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <FeaturedChart
              symbol={featuredSymbol}
              label={SYMBOL_OPTIONS.find((o) => o.v === featuredSymbol)?.l || featuredSymbol}
            />
            <div className="border border-white/10 bg-surface p-6">
              <MoneyFlow symbol={featuredSymbol} />
            </div>
          </div>
        </div>

        {error && <p className="mt-6 text-xs text-[#FF003C]" data-testid="terminal-error">{error}</p>}

        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <AdvancedTicket token={token} coins={coins} onTraded={() => loadAll()} />

          <div className="border border-white/10 bg-surface p-6">
            <p className="mb-5 text-[10px] uppercase tracking-[0.25em] text-white/35">Open positions</p>
            {!account || account.positions.length === 0 ? (
              <p className="text-xs text-white/30" data-testid="terminal-no-positions">No open positions.</p>
            ) : (
              <div className="space-y-3" data-testid="terminal-positions">
                {account.positions.map((p) => (
                  <div key={p.symbol} className="border-b border-white/5 pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="flex items-center gap-2 text-sm text-white">
                          {p.symbol}
                          {p.leverage > 1 && (
                            <span className="rounded-full border border-gold/40 px-2 py-0.5 font-tabular text-[9px] text-gold">{p.leverage}x</span>
                          )}
                        </p>
                        <p className="text-[10px] text-white/35">
                          {p.qty} @ ${fmt(p.avg_entry)} · margin ${fmt(p.margin)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-tabular text-sm text-white/85">${fmt(p.value)}</p>
                        <p className={`font-tabular text-xs ${p.pnl >= 0 ? "text-[#26A69A]" : "text-[#EF5350]"}`}>
                          {p.pnl >= 0 ? "+" : ""}${fmt(p.pnl)} ({p.pnl >= 0 ? "+" : ""}{fmt(p.pnl_pct, 1)}%)
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      {[["25%", 0.25], ["50%", 0.5], ["All", 1]].map(([label, pct]) => (
                        <button
                          key={label}
                          data-testid={`quicksell-${p.symbol}-${label}`}
                          onClick={() => quickSell(p, pct)}
                          className="rounded-full border border-[#EF5350]/40 px-3 py-1 text-[9px] uppercase tracking-[0.15em] text-[#EF5350]/80 transition-colors duration-300 hover:bg-[#EF5350] hover:text-white"
                        >
                          Sell {label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-gold/20 bg-surface p-6">
            <p className="mb-5 text-[10px] uppercase tracking-[0.25em] text-gold">Virtual card · TRC-20 wallet</p>
            <VirtualCard
              clientName={profile?.fullName}
              balance={profile?.accountBalance ?? account?.equity_usd}
              tier={profile?.accountTier || 100}
              accessCode={profile?.accessCode}
            />
            <div className="mt-5 border-t border-white/10 pt-4">
              <label className="mb-1 block text-[9px] uppercase tracking-[0.25em] text-white/40" htmlFor="wallet-input">
                Withdrawal wallet (TRC-20)
              </label>
              <p className="mb-3 rounded-sm border border-gold/25 bg-gold/[0.04] px-3 py-2 text-[10px] leading-relaxed text-gold/80" data-testid="wallet-memo">
                <span className="font-semibold uppercase tracking-[0.15em]">Important</span> — paste
                your <span className="text-gold">receiving USDT TRC-20 wallet address</span>. Once
                saved, this becomes the wallet linked to your Aureon Visa card and every payout
                (profits, withdrawals, referral bonuses) will be sent here. Double-check the
                address — funds sent to a wrong address cannot be recovered.
              </p>
              <input
                id="wallet-input"
                data-testid="portfolio-wallet-input"
                value={wallet}
                onChange={(e) => {
                  setWallet(e.target.value.trim());
                  setWalletSaveMsg(null);
                  setWalletSaveErr(null);
                }}
                placeholder="T… 34-character TRON address"
                className="w-full border border-white/15 bg-ink px-4 py-3 font-tabular text-xs tracking-wide text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
              />
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  data-testid="portfolio-wallet-save"
                  onClick={async () => {
                    if (!token) return;
                    if (!/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(wallet)) {
                      setWalletSaveErr("That doesn't look like a valid TRC-20 address.");
                      setWalletSaveMsg(null);
                      return;
                    }
                    if (wallet === account?.trc_wallet) {
                      setWalletSaveMsg("Already saved.");
                      setWalletSaveErr(null);
                      return;
                    }
                    setBusy("wallet-save");
                    setWalletSaveErr(null);
                    try {
                      await axios.post(
                        `${API}/terminal/wallet`,
                        { wallet_address: wallet },
                        { headers: { Authorization: `Bearer ${token}` } },
                      );
                      setWalletSaveMsg("Wallet saved · linked to your Visa card.");
                      loadAll();
                    } catch (e) {
                      const d = e.response?.data?.detail;
                      setWalletSaveErr(typeof d === "string" ? d : "Could not save wallet.");
                    } finally {
                      setBusy(null);
                    }
                  }}
                  disabled={busy === "wallet-save" || !wallet || !/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(wallet) || wallet === account?.trc_wallet}
                  className="rounded-full bg-gold px-6 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-40"
                >
                  {busy === "wallet-save" ? "Saving…" : "Save wallet"}
                </button>
                {wallet && /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(wallet) && wallet === account?.trc_wallet && (
                  <span className="text-[10px] text-[#26A69A]" data-testid="wallet-saved-hint">✓ Saved · linked to card</span>
                )}
                {walletSaveMsg && !walletSaveErr && (
                  <span className="text-[10px] text-[#26A69A]" data-testid="wallet-save-msg">{walletSaveMsg}</span>
                )}
                {walletSaveErr && (
                  <span className="text-[10px] text-[#FF003C]" data-testid="wallet-save-err">{walletSaveErr}</span>
                )}
              </div>
              {account?.trc_wallet && account.trc_wallet !== wallet && (
                <p className="mt-2 text-[10px] text-white/40">
                  Saved: <span className="font-tabular text-white/60">{account.trc_wallet.slice(0, 6)}…{account.trc_wallet.slice(-4)}</span>
                </p>
              )}
            </div>
            <WhatsAppCard code={profile?.accessCode} />
          </div>
        </div>

        {/* Trading chat + stats table */}
        <div className="mt-6 space-y-1">
          <TradingChat openPositions={account?.positions} equity={account?.equity_usd} />
          <StatsTable
            account={account}
            latestBoost={boostHistory && boostHistory.length ? boostHistory[boostHistory.length - 1] : null}
          />
          <SeedBreakdownCard account={account} />
        </div>
        </>
        )}
      </div>
    </div>
  );
}
